<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <!-- Form Section -->
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <div class="ppv2-space-y-6">
                <!-- Règles de sélection -->
                <div
                    v-if="!hasSelectionRules"
                    class="ppv2-relative"
                    :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20) }"
                >
                    <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                        <BaseSwitch
                            v-model="hasSelectionRules"
                            :label="$t('package.step5.selectionRules')"
                            @change="activeField = 'selectionRules'"
                        />
                    </div>
                </div>

                <DashedCard v-else :marginTop="37" :marginBottom="30" :custom-style="{ padding: normalizeSize(24, true) }">
                    <template #title>
                        <BaseSwitch
                            v-model="hasSelectionRules"
                            :label="$t('package.step5.selectionRules')"
                            @change="activeField = 'selectionRules'"
                        />
                    </template>
                    <template #content>
                        <div class="ppv2-grid md:ppv2-grid-cols-2 ppv2-grid-cols-1 ppv2-gap-6 ppv2-w-full">
                            <!-- Nombre min -->
                            <div>
                                <BaseSelect
                                    :label="$t('package.step5.minItems')"
                                    v-model="parameters.item_qty_min"
                                    :options="qtyOptions"
                                    compact
                                    :placeholder="$t('package.step5.select')"
                                    class="transparent-select ppv2-w-full"
                                    class-added="!ppv2-rounded-[4px] !ppv2-h-[30px]"
                                    :error="errors.item_qty_min"
                                    :customSizeLabel="14"
                                    @open="activeField = 'selectionRules'"
                                    @input="clearError('item_qty_max')"
                                    :floatingLabel="true"
                                />
                            </div>

                            <!-- Nombre max -->
                            <div>
                                <BaseSelect
                                    :label="$t('package.step5.maxItems')"
                                    v-model="parameters.item_qty_max"
                                    :options="qtyOptions"
                                    compact
                                    :placeholder="$t('package.step5.select')"
                                    class="transparent-select ppv2-w-full"
                                    class-added="!ppv2-rounded-[4px] !ppv2-h-[30px]"
                                    :error="errors.item_qty_max"
                                    :customSizeLabel="14"
                                    @open="activeField = 'selectionRules'"
                                    @input="clearError('item_qty_max')"
                                    :floatingLabel="true"
                                />
                            </div>
                        </div>
                    </template>
                </DashedCard>

                <!-- Délai entre activités -->
                <div
                    v-if="!hasBreakTime"
                    class="ppv2-relative"
                    :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20) }"
                >
                    <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                        <BaseSwitch
                            v-model="hasBreakTime"
                            :label="$t('package.step5.breakTime')"
                            @change="activeField = 'breakTime'"
                        />
                    </div>
                </div>

                <DashedCard
                    v-else
                    :marginTop="37"
                    :marginBottom="30"
                    :custom-style="{ padding: normalizeSize(24, true), justifyContent: 'center' }"
                >
                    <template #title>
                        <BaseSwitch
                            v-model="hasBreakTime"
                            :label="$t('package.step5.breakTime')"
                            @change="activeField = 'breakTime'"
                        />
                    </template>
                    <template #content>
                        <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-justify-between ppv2-text-black">
                            <label
                                class="ppv2-truncate poppinsRegular"
                                :style="{
                                    fontSize: normalizeFontSize(14),
                                    width: `calc(100% - ${normalizeSize(215, true)}px)`,
                                }"
                            >
                                {{ $t('package.step5.breakDuration') }}<span class="ppv2-text-[#7D2DBD]">*</span>
                            </label>
                            <div class="ppv2-mr-3" :style="{ width: normalizeSize(160, true) }">
                                <CustomInput
                                    v-model="parameters.slot_succession"
                                    type="number"
                                    :min="1"
                                    :required="true"
                                    :right-icon="$t('package.step5.minutes')"
                                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                    classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg"
                                    :state="
                                        errors.slot_succession ? 'error' : parameters.slot_succession > 0 ? 'valid' : 'default'
                                    "
                                    :errorText="errors.slot_succession"
                                    @focus="activeField = 'breakTime'"
                                    @input="clearError('slot_succession')"
                                    :floatingLabel="true"
                                    :customSizeLabel="13"
                                />
                            </div>
                        </div>
                    </template>
                </DashedCard>
            </div>

            <!-- Navigation Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': !isCreationMode }">
                <BackButton v-if="isCreationMode" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="button" @click="submitForm" variant="primary" :loading="loading">
                    {{ isCreationMode ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>

        <!-- Preview Panel -->
        <PreviewPackage :active-field="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import HeaderStep from './HeaderStep.vue'
import PreviewPackage from './PreviewPackage.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import SelectableList from '../../../Shared/SelectableList.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'PackageStep5',
    components: {
        HeaderStep,
        PreviewPackage,
        BaseButton,
        BackButton,
        SelectableList,
        DashedCard,
        BaseSwitch,
        BaseSelect,
        CustomInput,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        isCreationMode: { type: Boolean, default: false },
    },
    data() {
        return {
            activeField: '',
            errors: {},
            initialDataString: null,
            hasSelectionRules: false,
            hasBreakTime: false,

            multiParticipantsOptions: [
                {
                    label: this.$t('package.step5.differentParticipants.yes'),
                    value: true,
                    subtitle: this.$t('package.step5.differentParticipants.yesSubtitle'),
                },
                {
                    label: this.$t('package.step5.differentParticipants.no'),
                    value: false,
                    subtitle: this.$t('package.step5.differentParticipants.noSubtitle'),
                },
            ],
        }
    },
    computed: {
        ...mapGetters('package', ['parameters', 'selectedType', 'isEditMode', 'loading']),
        parameters: {
            get() {
                return this.$store.getters['package/parameters']
            },
            set(value) {
                this.$store.dispatch('package/setParameters', value)
            },
        },
        comboItemsCount() {
            return this.$store.getters['package/comboItems']?.length || 0
        },
        qtyOptions() {
            const count = this.comboItemsCount
            if (count === 0) return []
            return Array.from({ length: count }, (_, i) => ({
                label: (i + 1).toString(),
                value: i + 1,
            }))
        },
    },
    watch: {
        comboItemsCount(newCount) {
            if (newCount > 0) {
                const params = this.parameters
                const updates = {}
                if (params.item_qty_min && params.item_qty_min > newCount) {
                    updates.item_qty_min = newCount
                }
                if (params.item_qty_max && params.item_qty_max > newCount) {
                    updates.item_qty_max = newCount
                }
                if (Object.keys(updates).length > 0) {
                    this.parameters = { ...params, ...updates }
                }
            }
        },
        hasSelectionRules(val) {
            if (!val) {
                this.parameters = {
                    ...this.parameters,
                    item_qty_min: null,
                    item_qty_max: null,
                }
            }
        },
        hasBreakTime(val) {
            if (!val) {
                this.parameters = {
                    ...this.parameters,
                    slot_succession: 0,
                }
                this.clearError('slot_succession')
            }
        },
    },
    mounted() {
        if (this.parameters.item_qty_min || this.parameters.item_qty_max) {
            this.hasSelectionRules = true
        }
        if (this.parameters.slot_succession > 0) {
            this.hasBreakTime = true
        }
        this.initialDataString = this.getFormDataString()
    },
    methods: {
        normalizeFontSize,
        ...mapActions('package', ['setParameters', 'savePackage']),
        normalizeSize,
        normalizeSizeVertical,

        getFormDataString() {
            return JSON.stringify({
                parameters: this.parameters,
                hasSelectionRules: this.hasSelectionRules,
                hasBreakTime: this.hasBreakTime,
            })
        },

        hasChanges() {
            return this.initialDataString !== this.getFormDataString()
        },

        resetInitialState() {
            this.initialDataString = this.getFormDataString()
        },

        async submitForm() {
            this.errors = {}

            if (this.hasSelectionRules) {
                const comboCount = this.comboItemsCount

                if (this.parameters.item_qty_min && comboCount > 0 && this.parameters.item_qty_min > comboCount) {
                    this.$set(
                        this.errors,
                        'item_qty_min',
                        `Le nombre minimum ne peut pas dépasser le nombre d'expériences sélectionnées (${comboCount}).`
                    )
                    return
                }

                if (this.parameters.item_qty_max && comboCount > 0 && this.parameters.item_qty_max > comboCount) {
                    this.$set(
                        this.errors,
                        'item_qty_max',
                        `Le nombre maximum ne peut pas dépasser le nombre d'expériences sélectionnées (${comboCount}).`
                    )
                    return
                }

                if (
                    this.parameters.item_qty_min &&
                    this.parameters.item_qty_max &&
                    this.parameters.item_qty_max < this.parameters.item_qty_min
                ) {
                    this.$set(this.errors, 'item_qty_max', this.$t('package.step5.errors.maxItemsInvalid'))
                    return
                }
            }

            if (this.hasBreakTime && (!this.parameters.slot_succession || this.parameters.slot_succession <= 0)) {
                this.errors.slot_succession = this.$t('package.step5.errors.breakDurationRequired')
                return
            }

            try {
                await this.savePackage()
                this.resetInitialState()
                if (!this.isCreationMode) {
                    this.$toast.success(this.$t('package.notifications.success_update'))
                }
                if (this.isCreationMode) {
                    this.$emit('next-step')
                }
            } catch (error) {
                this.$toast.error(error?.response?.data?.message || this.$t('package.notifications.saveErrorDefault'))
            }
        },

        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },

        async saveStep(redirect = false) {
            try {
                await this.savePackage()
                this.resetInitialState()
                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                }
                return true
            } catch (error) {
                if (error.response && (error.response.status === 422 || error.response.status === 500)) {
                    let message = this.$t('package.notifications.saveErrorDefault')
                    if (error.response.data?.errors) {
                        const firstErrorKey = Object.keys(error.response.data.errors)[0]
                        message = error.response.data.errors[firstErrorKey][0]
                    } else if (error.response.data?.message) {
                        message = error.response.data.message
                    }
                    this.$toast.error(message)
                } else {
                    this.$toast.error(this.$t('package.notifications.saveErrorDefault'))
                }
                return false
            }
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}
</style>
